import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { RouteReuseStrategy, provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { UnEcranParDossierStrategy } from './core/routing/un-ecran-par-dossier.strategy';
import { estRetourDeKeycloak, retablirAdresseDemandee } from './core/routing/retour-apres-connexion';
import {
  OidcSecurityService,
  authInterceptor as oidcAuthInterceptor,
  provideAuth,
} from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';

import {
  provideLucideIcons,
  LucideLayoutDashboard,
  LucideCalendar,
  LucideCalendarDays,
  LucideFileSpreadsheet,
  LucideInbox,
  LucideBriefcase,
  LucideBuilding2,
  LucideDatabase,
  LucideHelpCircle,
  LucideBellRing,
  LucideBell,
  LucideFolderOpen,
  LucideUser,
  LucideArchive,
  LucideCalendarPlus,
  LucideClockAlert,
  LucideFileClock,
  LucideFileSignature,
  LucideGauge,
  LucideLibrary,
  LucideListChecks,
  LucideSlidersHorizontal,
  LucideCrown,
  LucideUsers,
  LucideUserCheck,
  LucideBuilding,
  LucideFileCheck,
  LucideBarChart3,
  LucideChartColumn,
  LucideLayers,
  LucideSettings,
  LucideWrench,
  LucideInfo,
  LucideTrendingUp,
  LucideHistory,
  LucideAlertTriangle,
  LucideGitBranch,
  LucidePencil,
  LucideCheck,
  LucideX,
  LucidePlus,
  LucideArrowLeft,
  LucideArrowRight,
  LucideLogOut,
  LucideMenu,
  LucideUpload,
  LucideSliders,
  LucideUserMinus,
  LucideSearch,
  LucideDownload,
  LucideTrash2,
  LucideTag,
  LucideFileText,
  LucideFilter,
  LucidePaperclip,
  LucideEye,
  LucideLock,
  LucideGlobe,
  LucideXCircle,
  LucideBan,
  LucideRotateCcw,
  LucideGraduationCap,
  LucideCoins,
  LucideNewspaper,
  LucideLifeBuoy,
  LucideEyeOff,
  LucideChevronUp,
  LucidePin,
  LucidePlusCircle,
  LucideFolderSearch,
  LucideStar,
  LucideCheckCircle,
  LucideMegaphone,
  LucideBookOpen,
  LucideFolder,
  LucideChevronDown,
  LucideChevronLeft,
  LucideChevronRight,
  LucideSend,
  LucideMessageCircle,
  LucideMessagesSquare,
  LucideArrowLeftRight,
  LucideKeyRound,
  LucideCopy,
  LucideCloudUpload,
  LucideWifiOff,
  LucideRefreshCw,
  LucideLoaderCircle,
  LucideFileImage,
  LucideFileArchive,
  LucideFile,
  LucideCircleCheck,
  LucideCircleAlert,
  LucideExternalLink,
  LucideHash,
} from '@lucide/angular';

import { routes } from './app.routes';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { uploadProgressInterceptor } from './shared/ged/upload/ged-upload';
import { environment } from '../environments/environment';

/**
 * Configuration applicative (APIs standalone Angular 21).
 *  - Zoneless, router lazy.
 *  - Auth OIDC (Authorization Code + PKCE) sur le realm Keycloak `cce`, client public `cce-frontend`.
 *    Le login réel est la page hébergée par Keycloak (thème `cce`).
 *  - `checkAuth()` est exécuté UNE FOIS au démarrage (app initializer) : il consomme le retour de
 *    connexion (`?code=`) AVANT que le routeur n'évalue la garde → pas de boucle de redirection.
 *  - HttpClient : intercepteur OIDC (Bearer sur les `secureRoutes` = l'API) + gestion d'erreurs.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    // onSameUrlNavigation:'reload' → re-cliquer le lien de menu de la route courante émet à nouveau
    // un événement de navigation (les pages à état interne, ex. rapport annuel, peuvent revenir à leur liste).
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ onSameUrlNavigation: 'reload' })),
    // Un dossier, un écran : une page marquée `unEcranParDossier` est RECRÉÉE quand on passe d'un dossier à un
    // autre (clic d'une notification depuis une page du même type) — voir UnEcranParDossierStrategy.
    { provide: RouteReuseStrategy, useClass: UnEcranParDossierStrategy },
    // ⚠️ L'ORDRE PORTE LE REJEU : l'intercepteur d'erreur est en AMONT, celui du token en AVAL.
    // Quand un 401 se répare par un renouvellement de session, la requête se REJOUE depuis
    // l'intercepteur d'erreur — et c'est parce que l'attacheur de token est en aval que le rejeu
    // repart avec le jeton FRAIS. Inversés, le rejeu repartirait avec le jeton périmé qui vient
    // d'échouer.
    //
    // `uploadProgressInterceptor` en TÊTE : il n'agit que sur les envois GED qui le demandent
    // (jeton `GED_UPLOAD_PROGRESS`) et doit voir les événements d'envoi du rejeu éventuel.
    provideHttpClient(withInterceptors([uploadProgressInterceptor, errorInterceptor, oidcAuthInterceptor()])),
    provideAuth({
      config: {
        authority: environment.oidcIssuer,
        redirectUrl: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        clientId: environment.oidcClientId,
        scope: 'openid profile email',
        responseType: 'code',
        silentRenew: false,
        useRefreshToken: true,
        // Renouveler 30 s AVANT l'expiration : rétrécit la fenêtre où un poll part avec un jeton
        // périmé (le 401 de course qui faisait « recharger la page toute seule », staging
        // 2026-09-01). L'intercepteur répare cette course de toute façon — ceci la rend rare.
        renewTimeBeforeTokenExpiresInSeconds: 30,
        // L'intercepteur n'ajoute le token QUE sur ces URLs (l'API du SI).
        secureRoutes: [environment.apiBaseUrl],
        // ⚠️ La librairie ne NAVIGUE PAS après le retour de Keycloak. Par défaut elle lance
        // `navigateByUrl('/')`, qui faisait la course avec la page rétablie par `retablirAdresseDemandee`
        // et l'emportait selon le timing : le lien profond retombait sur le tableau de bord. Sans elle, la
        // navigation initiale du routeur lit l'adresse rétablie — ou `/`, l'atterrissage par rôle d'avant.
        triggerAuthorizationResultEvent: true,
      },
    }),
    // Traite le callback / restaure la session avant le routage (évite la boucle de redirection), puis
    // rétablit la page demandée avant le détour par Keycloak — sans quoi un lien ouvert dans un nouvel
    // onglet atterrissait sur le tableau de bord (voir retour-apres-connexion.ts).
    provideAppInitializer(() => {
      const retourDeKeycloak = estRetourDeKeycloak();
      return firstValueFrom(inject(OidcSecurityService).checkAuth()).then(({ isAuthenticated }) =>
        retablirAdresseDemandee(isAuthenticated, retourDeKeycloak),
      );
    }),
    // Fournit les icônes Lucide à l'application entière pour le chargement dynamique.
    provideLucideIcons(
      LucideArrowLeftRight,
      LucideKeyRound,
      LucideCopy,
      LucideLayoutDashboard,
      LucideCalendar,
      LucideCalendarDays,
      LucideFileSpreadsheet,
      LucideInbox,
      LucideBriefcase,
      LucideBuilding2,
      LucideDatabase,
      LucideHelpCircle,
      LucideBellRing,
      LucideBell,
      LucideFolderOpen,
      LucideUser,
      // Icônes des menus ajoutés (cycle de vie des séances, files du Président, jalons
      // Reporting). À FOURNIR ICI, sinon elles ne s'affichent pas : `LucideDynamicIcon`
      // résout un nom dans l'ensemble FOURNI, pas dans la bibliothèque entière — un nom
      // pourtant valide reste alors invisible, sans erreur.
      LucideArchive,
      LucideCalendarPlus,
      // Mandats échus CAEEP : l'échéance qui appelle une action.
      LucideClockAlert,
      LucideFileClock,
      LucideFileSignature,
      LucideGauge,
      LucideLibrary,
      LucideListChecks,
      LucideSlidersHorizontal,
      LucideCrown,
      LucideUsers,
      LucideUserCheck,
      LucideBuilding,
      LucideFileCheck,
      LucideBarChart3,
      LucideChartColumn,
      LucideLayers,
      LucideSettings,
      LucideWrench,
      LucideInfo,
      LucideTrendingUp,
      LucideHistory,
      LucideAlertTriangle,
      LucideGitBranch,
      LucidePencil,
      LucideCheck,
      LucideX,
      LucidePlus,
      LucideArrowLeft,
      LucideArrowRight,
      LucideLogOut,
      LucideMenu,
      LucideUpload,
      LucideSliders,
      LucideUserMinus,
      LucideSearch,
      LucideDownload,
      LucideTrash2,
      LucideTag,
      LucideFileText,
      LucideFilter,
      LucidePaperclip,
      // Actions du panneau documentaire partagé — absentes jusqu'ici, donc INVISIBLES dans les
      // quatre contextes qui l'utilisent (dossier permanent, mission, séance, publication).
      LucideEye,
      LucideLock,
      LucideGlobe,
      LucideXCircle,
      LucideBan,
      LucideRotateCcw,
      LucideGraduationCap,
      LucideCoins,
      LucideNewspaper,
      LucideLifeBuoy,
      LucideEyeOff,
      LucideChevronUp,
      LucidePin,
      LucidePlusCircle,
      LucideFolderSearch,
      LucideStar,
      LucideCheckCircle,
      LucideMegaphone,
      LucideBookOpen,
      LucideFolder,
      LucideChevronDown,
      LucideChevronLeft,
  LucideChevronRight,
      LucideSend,
      // فضاء التواصل (PR-04) — entrée de menu des deux espaces.
      LucideMessageCircle,
      LucideMessagesSquare,
      // Téléversement GED (zone de dépôt, file d'envoi, aperçu) — refonte UX du 2026-09-30.
      LucideCloudUpload,
      LucideWifiOff,
      LucideRefreshCw,
      LucideLoaderCircle,
      LucideFileImage,
      LucideFileArchive,
      LucideFile,
      LucideCircleCheck,
      LucideCircleAlert,
      LucideExternalLink,
      LucideHash,
    ),
  ],
};
